import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../../imageService';
import { CARD_LABELS, drawPrediction } from '../../predictions';

// Кількість карт у колоді.
const CARD_COUNT = 7;
// Максимальний кут (градуси) лівого віяла в IDLE-анімації (до кліку). Від'ємне = вліво.
const FAN_LEFT_MAX_DEG = 30;
// Крок горизонтального розсування карт у сторони під час тасування (px на одну "сходинку").
const SHUFFLE_SPREAD_STEP_PX = 34;

// На скільки px вгору піднімаються 3 обрані карти.
const LIFT_UP_PX = 200;
// Які карти піднімаються догори і на яку X-позицію ряду (px від центру).
// null = картка НЕ піднімається (лишається в розкладці). Тут: i2 → ліворуч, i3 → праворуч, i6 → центр.
const LIFT_ROW_X = [null, null, -105, 105, null, null, 0];
// Крок "драбинки" (px) у фінальній колоді — на скільки кожна наступна карта зсунута вниз.
const STAIR_STEP_PX = 7;
// На скільки px вниз з'їжджає вся колода в кінці.
const DECK_DROP_PX = 140;

// --- Тривалості фаз тасування (мс) ---
const CLOSE_MS = 900; // закриття колоди в одну стопку
const FAN_MS = 840; // розсування однієї карти в сторону
const FAN_STAGGER_MS = 260; // затримка між "шарами" при розсуванні (стаґер)
const FAN_HOLD_MS = 500; // пауза в розкритому стані перед підйомом
const LIFT_MS = 680; // підйом 3 карт догори
const LIFT_PAUSE_MS = 840; // пауза вгорі
const COLLAPSE_MS = 900; // складання всіх карт назад у стопку
const DECK_DROP_MS = 760; // зсув колоди вниз "драбинкою"
const CONTROLS_HIDE_MS = 2000; // плавне опускання блока кнопки за межі екрана на старті тасування
const CONTROLS_SHOW_MS = 1000; // повернення блока кнопки на екран після тасування

// --- Результат (передбачення) ---
const RESULT_ROW_X_PX = 70; // на скільки px розходяться бічні карти результату (виходять за край)
const RESULT_TILT_DEG = 20; // нахил бічних карт у бік вильоту (ліва -, права +)
const RESULT_SIDE_DROP_PX = 10; // наскільки бічні карти нижче за центральну
const RESULT_DECK_OFFSET_PX = 300; // звідки (знизу, від колоди) вилітають карти результату
const RESULT_EMERGE_MS = 1240; // виліт однієї карти вгору
const RESULT_EMERGE_STAGGER_MS = 600; // затримка між вильотами карт результату
const RESULT_SPREAD_MS = 1240; // розведення бічних карт у сторони
const PANEL_SLIDE_MS = 1500; // виїзд плашки знизу
const PANEL_COLLAPSE_MS = 450; // згортання / розгортання плашки
const PANEL_PEEK_PX = 45; // скільки px плашки видно у згорнутому стані

const cards = Array.from({ length: CARD_COUNT });

// Кут кожної карти у IDLE-віялі: верхня (остання) = 0°, решта віялом вліво.
const getIdleAngle = (index) =>
  (-FAN_LEFT_MAX_DEG * (CARD_COUNT - 1 - index)) / (CARD_COUNT - 1);

// X-зміщення кожної карти при розсуванні: 3 вліво, 3 вправо, центральна (i6) на місці.
const SHUFFLE_X = [
  -3 * SHUFFLE_SPREAD_STEP_PX,
  -2 * SHUFFLE_SPREAD_STEP_PX,
  -1 * SHUFFLE_SPREAD_STEP_PX,
  1 * SHUFFLE_SPREAD_STEP_PX,
  2 * SHUFFLE_SPREAD_STEP_PX,
  3 * SHUFFLE_SPREAD_STEP_PX,
  0
];
const SHUFFLE_ORDER = [0, 1, 2, 2, 1, 0, 0];
const SHUFFLE_ORDER_MAX = 2;
const SHUFFLE_Z = [1, 2, 3, 3, 2, 1, 4];

const cardImage = (key) => getImageFromS3(`/images/tarot/${key}.png`);

const ShuffleScreen = ({ onComplete }) => {
  const deckRef = useRef(null);
  const resultRef = useRef(null);
  const panelRef = useRef(null);
  // Блок із кнопкою тасування та підказкою (опускаємо його на час тасування).
  const controlsRef = useRef(null);
  const runningRef = useRef(false);
  const canceledRef = useRef(false);
  const timersRef = useRef([]);
  const [prediction, setPrediction] = useState(null);
  const [revealed, setRevealed] = useState(false); // чи користувач торкнувся, щоб відкрити карти результату
  const [panelCollapsed, setPanelCollapsed] = useState(false); // чи згорнута плашка результату

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      canceledRef.current = true;
      timers.forEach((id) => clearTimeout(id));
    };
  }, []);

  // Прогрів кешу 12 картинок результату (щоб при розкритті auto-ширина була вже відома).
  useEffect(() => {
    Object.keys(CARD_LABELS).forEach((key) => {
      const img = new Image();
      img.src = cardImage(key);
    });
  }, []);

  const delay = useCallback(
    (ms) =>
      new Promise((resolve) => {
        const id = setTimeout(resolve, ms);
        timersRef.current.push(id);
      }),
    []
  );

  useEffect(() => {
    // Виліт карт результату запускається лише після дотику користувача (revealed).
    if (!prediction || !revealed) return undefined;
    const container = resultRef.current;
    if (!container) return undefined;
    const panel = panelRef.current;
    const resultEls = Array.from(container.querySelectorAll('img'));

    const run = async () => {
      void container.offsetWidth;

      resultEls.forEach((el, i) => {
        el.style.transition = `transform ${RESULT_EMERGE_MS}ms ease, opacity ${RESULT_EMERGE_MS}ms ease`;
        el.style.transitionDelay = `${i * RESULT_EMERGE_STAGGER_MS}ms`;
        el.style.opacity = '1';
        el.style.transform = 'translate(-50%, 0px) rotate(0deg)';
      });
      await delay(RESULT_EMERGE_MS + 2 * RESULT_EMERGE_STAGGER_MS + 220);
      if (canceledRef.current) return;

      if (resultEls[0]) {
        resultEls[0].style.transition = `transform ${RESULT_SPREAD_MS}ms ease`;
        resultEls[0].style.transitionDelay = '0ms';
        resultEls[0].style.transform = `translate(calc(-50% - ${RESULT_ROW_X_PX}px), ${RESULT_SIDE_DROP_PX}px) rotate(${-RESULT_TILT_DEG}deg)`;
      }
      if (resultEls[1]) {
        resultEls[1].style.transition = `transform ${RESULT_SPREAD_MS}ms ease`;
        resultEls[1].style.transitionDelay = '0ms';
        resultEls[1].style.transform = `translate(calc(-50% + ${RESULT_ROW_X_PX}px), ${RESULT_SIDE_DROP_PX}px) rotate(${RESULT_TILT_DEG}deg)`;
      }
      await delay(RESULT_SPREAD_MS + 120);
      if (canceledRef.current) return;

      if (panel) {
        panel.style.transition = `transform ${PANEL_SLIDE_MS}ms ease`;
        panel.style.transform = 'translateY(0px)';
      }
    };
    run();
    return undefined;
  }, [prediction, revealed, delay]);

  // Згорнути / розгорнути плашку результату (імперативно, щоб не конфліктувати з виїздом).
  const togglePanel = () => {
    const panel = panelRef.current;
    if (!panel) return;
    const next = !panelCollapsed;
    setPanelCollapsed(next);
    panel.style.transition = `transform ${PANEL_COLLAPSE_MS}ms ease`;
    panel.style.transform = next
      ? `translateY(calc(100% - ${PANEL_PEEK_PX}px))`
      : 'translateY(0px)';
  };

  const runShuffle = async () => {
    const deck = deckRef.current;
    if (runningRef.current || !deck) return;
    runningRef.current = true;

    // Плавно опускаємо блок (кнопка тасування + підказка) за межі екрана на час тасування.
    const controls = controlsRef.current;
    if (controls) {
      const dropPx =
        window.innerHeight - controls.getBoundingClientRect().top + 40;
      controls.style.transition = `transform ${CONTROLS_HIDE_MS}ms ease`;
      controls.style.transform = `translateY(${dropPx}px)`;
    }

    const els = Array.from(deck.querySelectorAll('img'));

    els.forEach((el) => {
      const matrix = getComputedStyle(el).transform;
      let angle = 0;
      if (matrix && matrix !== 'none') {
        const p = matrix
          .replace(/matrix\(|\)/g, '')
          .split(',')
          .map(Number);
        angle = (Math.atan2(p[1], p[0]) * 180) / Math.PI;
      }
      el.style.animation = 'none';
      el.style.transition = 'none';
      el.style.transform = `translate(0px, 0px) rotate(${angle}deg)`;
    });
    void deck.offsetWidth;
    if (canceledRef.current) return;

    els.forEach((el) => {
      el.style.transition = `transform ${CLOSE_MS}ms ease`;
      el.style.transitionDelay = '0ms';
      el.style.transform = 'translate(0px, 0px) rotate(0deg)';
    });
    await delay(CLOSE_MS + 60);
    if (canceledRef.current) return;

    els.forEach((el, i) => {
      el.style.transition = `transform ${FAN_MS}ms ease`;
      el.style.transitionDelay = `${SHUFFLE_ORDER[i] * FAN_STAGGER_MS}ms`;
      el.style.zIndex = String(SHUFFLE_Z[i]);
      el.style.transform = `translate(${SHUFFLE_X[i]}px, 0px) rotate(0deg)`;
    });
    await delay(FAN_MS + SHUFFLE_ORDER_MAX * FAN_STAGGER_MS + FAN_HOLD_MS);
    if (canceledRef.current) return;

    els.forEach((el, i) => {
      if (LIFT_ROW_X[i] === null) return;
      el.style.transition = `transform ${LIFT_MS}ms ease`;
      el.style.transitionDelay = '0ms';
      el.style.zIndex = '10';
      el.style.transform = `translate(${LIFT_ROW_X[i]}px, ${-LIFT_UP_PX}px) rotate(0deg)`;
    });
    await delay(LIFT_MS + LIFT_PAUSE_MS);
    if (canceledRef.current) return;

    els.forEach((el) => {
      el.style.transition = `transform ${COLLAPSE_MS}ms ease`;
      el.style.transitionDelay = '0ms';
      el.style.transform = 'translate(0px, 0px) rotate(0deg)';
    });
    await delay(COLLAPSE_MS + 40);
    if (canceledRef.current) return;

    els.forEach((el, i) => {
      el.style.transition = `transform ${DECK_DROP_MS}ms ease`;
      el.style.transitionDelay = '0ms';
      el.style.zIndex = '';
      el.style.transform = `translate(0px, ${DECK_DROP_PX + i * STAIR_STEP_PX}px) rotate(0deg)`;
    });
    await delay(DECK_DROP_MS + 60);
    if (canceledRef.current) return;

    setPrediction(drawPrediction());

    // Повертаємо блок (кнопка + підказка) назад на екран перед відкриттям передбачень.
    // Даємо кадр на ре-рендер, щоб підказка вже показувала «відкрити карти».
    await delay(50);
    if (canceledRef.current) return;
    if (controls) {
      controls.style.transition = `transform ${CONTROLS_SHOW_MS}ms ease`;
      controls.style.transform = 'translateY(0px)';
    }
  };

  return (
    <div
      className={styles.screen}
      style={{
        backgroundImage: `url(${getImageFromS3('/images/tarot/bg.png')})`,
        backgroundPosition: 'center -60px'
      }}
    >
      <div className={styles.deckArea}>
        <div className={styles.deck} ref={deckRef}>
          {cards.map((_, i) => (
            <img
              key={i}
              className={styles.deckCard}
              style={{ '--fan-angle': `${getIdleAngle(i)}deg` }}
              src={getImageFromS3('/images/tarot/base-card.png')}
              alt=""
            />
          ))}
        </div>
        <div className={styles.deckControls} ref={controlsRef}>
          <button
            type="button"
            className={styles.shuffleButton}
            onClick={runShuffle}
            aria-label="Тасувати карти"
          >
            <img
              src={getImageFromS3('/images/tarot/shuffle-button.png')}
              alt=""
            />
          </button>
          {!revealed && (
            <p className={styles.hint}>
              Натисни, щоб
              <br />
              {prediction ? 'відкрити карти' : 'тасувати карти'}
            </p>
          )}
        </div>
      </div>
      {prediction && !revealed && (
        <button
          type="button"
          className={styles.revealTap}
          onClick={() => setRevealed(true)}
          aria-label="Відкрити карти"
        />
      )}
      {prediction && (
        <>
          <div className={styles.resultCards} ref={resultRef}>
            {prediction.cards.map((key, i) => (
              <img
                key={i}
                className={styles.resultCard}
                style={{
                  opacity: 0,
                  transform: `translate(-50%, ${RESULT_DECK_OFFSET_PX}px) rotate(0deg)`
                }}
                src={cardImage(key)}
                alt={CARD_LABELS[key]}
              />
            ))}
          </div>
          <div className={styles.panel} ref={panelRef}>
            <button
              type="button"
              className={styles.panelToggle}
              onClick={togglePanel}
              aria-label="Згорнути або розгорнути"
            >
              <div className={styles.panelLine} />
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M28 14C28 21.732 21.732 28 14 28C6.26801 28 0 21.732 0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14Z"
                  fill="#D9D9D9"
                  fill-opacity="0.2"
                />
                <path
                  d="M22.079 15.7091C22.5069 16.1416 22.5069 16.843 22.079 17.2756C21.6511 17.7081 20.9574 17.7081 20.5295 17.2756L13.9999 10.6742L7.47031 17.2756C7.04243 17.7081 6.34869 17.7081 5.92081 17.2756C5.49293 16.843 5.49293 16.1416 5.92081 15.7091L13.2251 8.32443C13.653 7.89185 14.3468 7.89186 14.7746 8.32443L22.079 15.7091Z"
                  fill="white"
                />
              </svg>
            </button>
            <ul className={styles.panelList}>
              {prediction.cards.map((key, i) => (
                <li key={i}>
                  <b>
                    {i + 1} карта: {CARD_LABELS[key]}
                  </b>
                </li>
              ))}
            </ul>
            <p className={styles.panelText}>{prediction.text}</p>
            <button
              type="button"
              className={styles.panelButton}
              style={{
                backgroundImage: `url(${getImageFromS3(
                  '/images/tarot/button-bg.png'
                )})`
              }}
              onClick={onComplete}
            >
              До продукту
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShuffleScreen;
